import React, {
  useEffect,
  useState,
  useReducer,
  useCallback,
  useRef,
  useMemo,
} from "react";

import {
  Backdrop,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  useMediaQuery,
  Divider,
  Box,
  LinearProgress,
  Popper,
  Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { red, grey, green } from "@material-ui/core/colors";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import SubdirectoryArrowLeftIcon from "@material-ui/icons/SubdirectoryArrowLeft";
import TrainIcon from "@material-ui/icons/Train";
import { Confirm, useNotify, useRedirect, Title } from "react-admin";
import CrewCard from "../../../components/common/CrewCard";
import {
  InboundState,
  InboundStatus,
  OutboundState,
  OutboundStatus,
  ERROR_MSG,
  UNSPOTTED_TRACK_ID,
} from "../../../utils/constants";
import { logError } from "../../../utils/appUtils";
import { isOutbound as isOutboundUtil } from "../../../utils/trackCarPersistence";
import { useTrackDragDrop } from "../../../managerApp/resources/trackCars/useTrackDragDrop";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { TrackDndProvider } from "../../../components/tracks/TrackDndProvider";
import { RailcarItem } from "./components/RailcarItem";
import { TrackControl } from "./components/TrackControl";
import { TrackLegend } from "../../../components/tracks/TrackLegend";
import { TrackBucket } from "./components/TrackBucket";
import {
  TrackLocationProvider,
  useTrackLocationContext,
} from "./TrackLocationContext";
import { useTrackDnd } from "../../../components/tracks/TrackDndProvider";
import { setAccountColorMap } from "../../../utils/trackListUtils";
import { useTrackLocationFilters } from "./hooks/useTrackLocationFilters";
import { useTrackLocationView } from "./hooks/useTrackLocationView";
import { useTrackLocationSelection } from "./hooks/useTrackLocationSelection";
import { useTrackManagement } from "../../../utils/useTrackManagement";
import { useTrackLocationOperations } from "./hooks/useTrackLocationOperations";
import { trackReducer, createTrackActions } from "../../../utils/trackReducer";
import {
  getSelectedRailcarContainerStyle,
  getSelectedRailcarIconColor,
  getSelectedRailcarSecondaryTextColor,
} from "../../../utils/selectedRailcarStyle";
import {
  selectionReducer,
  createSelectionActions,
} from "./reducers/selectionReducer";
import {
  getShipmentClassName,
  getShipmentIcon,
  getIconClassName,
  getShipmentStatusFromTrackCar,
  getRailCarNumberFromTrackCar,
  isTrackDisabled,
} from "./utils/trackLocationUtils";
import { ErrorBoundary } from "../../../components/common/ErrorBoundary";

const UNSPOTTED_PAGE_SIZE = 200;

const useStyles = makeStyles((theme) => ({
  trackContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    border: "1px dashed",
    borderRadius: theme.spacing(1),
    borderColor: grey[400],
    padding: theme.spacing(1),
    minHeight: theme.spacing(8),
  },
  trackLocation: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: theme.spacing(0.75),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    border: "1px solid",
    borderColor: grey[600],
    borderRadius: theme.spacing(1),
    position: "relative",
    overflow: "hidden",
    touchAction: "none", // Prevent default touch behaviors
    WebkitTouchCallout: "none", // Disable iOS callout
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent", // Remove tap highlight on mobile
    backfaceVisibility: "hidden", // Improve touch performance
    "& *": {
      // Prevent text selection on all children
      WebkitUserSelect: "none",
      MozUserSelect: "none",
      msUserSelect: "none",
      userSelect: "none",
      WebkitTouchCallout: "none",
    },
    "&:hover": {
      cursor: "grab",
    },
  },
  trackLocationSelected: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: theme.spacing(0.75),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    ...getSelectedRailcarContainerStyle(theme),
    borderRadius: theme.spacing(1),
    position: "relative",
    overflow: "hidden",
    touchAction: "none",
    WebkitTouchCallout: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent",
    backfaceVisibility: "hidden",
    "& *": {
      WebkitUserSelect: "none",
      MozUserSelect: "none",
      msUserSelect: "none",
      userSelect: "none",
      WebkitTouchCallout: "none",
    },
    "& .MuiTypography-caption": {
      color: getSelectedRailcarSecondaryTextColor(theme),
    },
    "& .MuiTypography-body2 svg": {
      color: getSelectedRailcarIconColor(),
    },
  },
  trackLocationCompleted: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: theme.spacing(0.75),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    border: "1px solid",
    borderColor: green[600],
    borderRadius: theme.spacing(1),
    position: "relative",
    overflow: "hidden",
    touchAction: "none",
    WebkitTouchCallout: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent",
    backfaceVisibility: "hidden",
    "& *": {
      WebkitUserSelect: "none",
      MozUserSelect: "none",
      msUserSelect: "none",
      userSelect: "none",
      WebkitTouchCallout: "none",
    },
  },
  trackLocationReleased: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: theme.spacing(0.75),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    border: "1px solid",
    borderColor: red[600],
    borderRadius: theme.spacing(1),
    position: "relative",
    overflow: "hidden",
    touchAction: "none",
    WebkitTouchCallout: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent",
    backfaceVisibility: "hidden",
    "& *": {
      WebkitUserSelect: "none",
      MozUserSelect: "none",
      msUserSelect: "none",
      userSelect: "none",
      WebkitTouchCallout: "none",
    },
  },
  trackLocationAccountMatched: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: theme.spacing(0.75),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    ...getSelectedRailcarContainerStyle(theme),
    borderRadius: theme.spacing(1),
    position: "relative",
    overflow: "hidden",
    touchAction: "none",
    WebkitTouchCallout: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent",
    backfaceVisibility: "hidden",
    "& *": {
      WebkitUserSelect: "none",
      MozUserSelect: "none",
      msUserSelect: "none",
      userSelect: "none",
      WebkitTouchCallout: "none",
    },
    "& .MuiTypography-caption": {
      color: getSelectedRailcarSecondaryTextColor(theme),
    },
    "& .MuiTypography-body2 svg": {
      color: getSelectedRailcarIconColor(),
    },
  },
  trackLocationCurrent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: theme.spacing(0.75),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    ...getSelectedRailcarContainerStyle(theme),
    borderRadius: theme.spacing(1),
    position: "relative",
    overflow: "hidden",
    touchAction: "none",
    WebkitTouchCallout: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent",
    backfaceVisibility: "hidden",
    "& *": {
      WebkitUserSelect: "none",
      MozUserSelect: "none",
      msUserSelect: "none",
      userSelect: "none",
      WebkitTouchCallout: "none",
    },
    "&:hover": {
      cursor: "grab",
    },
    "& .MuiTypography-caption": {
      color: getSelectedRailcarSecondaryTextColor(theme),
    },
    "& .MuiTypography-body2 svg": {
      color: getSelectedRailcarIconColor(),
    },
  },
  addButton: {
    marginTop: theme.spacing(1.25),
  },
  carName: {
    display: "flex",
    alignItems: "center",
    paddingTop: theme.spacing(0.5),
    paddingBottom: 0,
  },
  accountName: {
    fontSize: "0.65rem",
    color: theme.palette.text.secondary,
    paddingBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(3), // Offset for the icon
    marginTop: theme.spacing(-0.5),
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  },
  accountIndicator: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: theme.spacing(1),
    zIndex: 1,
  },
  productIndicator: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    width: theme.spacing(1),
    zIndex: 1,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
  greenIcon: {
    color: green[600],
  },
  redIcon: {
    color: red[600],
  },
  greyIcon: {
    color: grey[600],
  },
  whiteIcon: {
    color: getSelectedRailcarIconColor(),
  },
  trackControlContainer: {
    display: "flex",
    width: "100%",
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  trackControlRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    width: "100%",
  },
  trackControlField: {
    flex: "1 1 240px",
    minWidth: 180,
  },
  trackSelect: {
    minWidth: 240,
    width: "100%",
  },
  placeControls: {
    display: "flex",
    flexWrap: "wrap",
    width: "100%",
    gap: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  placeSelect: {
    flex: "1 1 260px",
    minWidth: 200,
  },
  placeButton: {
    flex: "0 0 180px",
    minWidth: 160,
  },
  actionIcons: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
}));

export const TrackLocation = (props) => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  // Use reducers for related state groups
  const [trackLocationState, trackLocationDispatch] = useReducer(trackReducer, {
    loading: true,
    tracks: [],
    selectedTrack: undefined,
    targetTrack: null,
    processedCarsMap: {},
    staleTracks: {},
  });

  const [selectionState, selectionDispatch] = useReducer(selectionReducer, {
    selectedShipmentIds: [],
    lastSelectedContext: null,
    selectedShipment: undefined,
  });

  // Create stable action functions using dispatch directly
  const trackLocationActions = useMemo(
    () => createTrackActions(trackLocationDispatch),
    [trackLocationDispatch],
  );
  const selectionActions = useMemo(
    () => createSelectionActions(selectionDispatch),
    [selectionDispatch],
  );

  // Destructure state for easier access
  const {
    loading,
    tracks,
    selectedTrack: reducerSelectedTrack,
    targetTrack: reducerTargetTrack,
    processedCarsMap,
    staleTracks,
  } = trackLocationState;

  const { selectedShipmentIds, lastSelectedContext, selectedShipment } =
    selectionState;

  const selectedIdsSet = useMemo(
    () => new Set(selectedShipmentIds),
    [selectedShipmentIds],
  );

  // Destructure actions for easier access
  const {
    setLoading,
    setTracks,
    setSelectedTrack: setSelectedTrackReducer,
    setTargetTrack: setTargetTrackReducer,
    setProcessedCarsMap,
    setStaleTracks,
  } = trackLocationActions;

  const {
    setSelectedShipmentIds,
    setLastSelectedContext,
    setSelectedShipment,
  } = selectionActions;

  // ============================================================================
  // LOCAL STATE & REFS
  // ============================================================================

  // Other state that doesn't need reducers
  const [colorsLoaded, setColorsLoaded] = useState(0);
  const [trackLoadingIds, setTrackLoadingIds] = useState(() => new Set());
  const handleAccountsLoaded = useCallback((accounts = []) => {
    const colorMap = {};
    accounts.forEach((account) => {
      if (account?.id && account?.color) {
        colorMap[account.id] = account.color;
      }
    });
    setAccountColorMap(colorMap);
    setColorsLoaded((prev) => prev + 1);
  }, []);
  const setTrackLoading = useCallback((trackLocationId, isLoading) => {
    if (!trackLocationId) return;
    setTrackLoadingIds((prev) => {
      const next = new Set(prev);
      if (isLoading) {
        next.add(trackLocationId);
      } else {
        next.delete(trackLocationId);
      }
      return next;
    });
  }, []);
  const clearTrackCars = useCallback(
    (trackLocationId) => {
      if (!trackLocationId) return;
      setProcessedCarsMap((prev) => {
        if (!prev) return prev;
        if (prev[trackLocationId] && prev[trackLocationId].length === 0)
          return prev;
        return { ...prev, [trackLocationId]: [] };
      });
      if (processedCarsMapRef.current) {
        processedCarsMapRef.current = {
          ...processedCarsMapRef.current,
          [trackLocationId]: [],
        };
      }
    },
    [setProcessedCarsMap],
  );
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState("");
  const [shipmentToDelete, setShipmentToDelete] = useState(null);
  const [selectedUnspottedId, setSelectedUnspottedId] = useState("");

  // STAFF LEVEL OPTIMIZATION: Singleton Tooltip state
  const [tooltipAnchor, setTooltipAnchor] = useState(null);
  const [tooltipTitle, setTooltipTitle] = useState("");

  const handleMouseEnter = useCallback((event, title) => {
    // Suppress tooltips during active drag for performance
    if (isDraggingRef.current) return;
    setTooltipAnchor(event.currentTarget);
    setTooltipTitle(title);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTooltipAnchor(null);
    setTooltipTitle("");
  }, []);

  // Track if a drag operation is in progress to prevent subscription updates from causing UI jitter
  const isDraggingRef = useRef(false);
  const handleDragEndFlushRef = useRef(null);
  // Ref to track if component is mounted (prevents state updates after unmount)
  const isMountedRef = useRef(true);
  // CRITICAL FIX: Cleanup timeout ref for delayed drag flag clearing
  const cleanupTimeoutRef = useRef(null);
  const lastInitKeyRef = useRef(null);
  const lastSelectedTrackLocationIdRef = useRef(null);
  const lastTargetTrackLocationIdRef = useRef(null);
  const lastChangedSideRef = useRef(null);
  const initCompleteRef = useRef(false);
  const suppressInitialSelectionFetchRef = useRef(true);
  // Store refs for values needed in drag handlers to avoid stale closures
  // Initialize with null - will be set in useEffect after state is defined
  const processedCarsMapRef = useRef(null);
  // Note: originalStateRef, latestStateRef are now managed by useTrackDragDrop hook

  // ============================================================================
  // CUSTOM HOOKS
  // ============================================================================

  // Use filter hook (uses local state for now, will be updated by selection hook)
  const {
    accountFilter,
    setAccountFilter,
    productFilter,
    setProductFilter,
    accountFilterId,
    displayProcessedCarsMap,
    displayProcessedCars,
  } = useTrackLocationFilters({
    processedCarsMap,
    selectedTrack: reducerSelectedTrack,
    targetTrack: reducerTargetTrack,
    setSelectedShipmentIds,
    setLastSelectedContext,
    setSelectedShipment,
  });
  const { currentShipment } = props;

  const selectedInventoryItem = props.selectedInventoryItem
    ? props.selectedInventoryItem
    : props.location?.state?.selectedInventoryItem;
  const defaultShipmentId = props.location?.state?.defaultShipmentId;
  const redirect = useRedirect();
  const notify = useNotify();
  const classes = useStyles();
  const isXSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  // Surface real errors to the user while still logging to backend
  const extractErrorMessage = useCallback((error) => {
    if (!error) return null;
    const gqlErrors =
      error?.errors || error?.response?.errors || error?.data?.errors;
    if (Array.isArray(gqlErrors) && gqlErrors.length) {
      return gqlErrors
        .map((e) => e?.message)
        .filter(Boolean)
        .join("; ");
    }
    if (error?.message) return error.message;
    return null;
  }, []);

  const notifyError = useCallback(
    (context, error) => {
      const detail = extractErrorMessage(error);
      const message = detail || ERROR_MSG;
      if (notify) notify(message, "error");
      logError(context, error, false, redirect); // log without double generic notify
    },
    [extractErrorMessage, notify, redirect],
  );

  // View flags only; selected/target track are in trackReducer (single source of truth)
  const { isTabletOrLandscape, showPlaceAction } = useTrackLocationView();

  const selectedTrack = reducerSelectedTrack;
  const targetTrack = reducerTargetTrack;
  const setSelectedTrack = setSelectedTrackReducer;
  const setTargetTrack = setTargetTrackReducer;

  // Ref for handlers that need current selected track without closure staleness
  const selectedTrackRef = useRef(selectedTrack);
  useEffect(() => {
    selectedTrackRef.current = selectedTrack;
  }, [selectedTrack]);

  // No-op: target track id is derived from targetTrack; TrackControl still receives this prop for API compatibility
  const setTargetTrackIdNoop = useCallback(() => {}, []);

  const handleTrackChangeForControl = useCallback(
    (trackId) => {
      const track = (tracks || []).find(
        (t) => t.id === trackId || String(t.id) === String(trackId),
      );
      lastChangedSideRef.current = "left";
      setSelectedTrackReducer(track);
    },
    [tracks, setSelectedTrackReducer],
  );

  const setTargetTrackForControl = useCallback(
    (track) => {
      lastChangedSideRef.current = "right";
      setTargetTrackReducer(track);
    },
    [setTargetTrackReducer],
  );

  // Sync selected/target track objects with the latest data in tracks array (e.g. capacity updates)
  useEffect(() => {
    if (!tracks || tracks.length === 0) return;

    const getCapacitySnapshot = (track) => {
      const loc = track?.locations?.items?.[0];
      if (!loc) return "none";
      return `${loc.capacityUsed ?? 0}-${loc.capacityAllocated ?? 0}-${
        loc.length ?? 0
      }`;
    };

    if (selectedTrack?.id) {
      const latest = tracks.find((t) => t.id === selectedTrack.id);
      if (
        latest &&
        getCapacitySnapshot(latest) !== getCapacitySnapshot(selectedTrack)
      ) {
        setSelectedTrackReducer(latest);
      }
    }

    if (targetTrack?.id) {
      const latest = tracks.find((t) => t.id === targetTrack.id);
      if (
        latest &&
        getCapacitySnapshot(latest) !== getCapacitySnapshot(targetTrack)
      ) {
        setTargetTrackReducer(latest);
      }
    }
  }, [
    tracks,
    selectedTrack,
    targetTrack,
    setSelectedTrackReducer,
    setTargetTrackReducer,
  ]);

  // Sync refs when values change (prevents handler recreation)
  // Initialize refs immediately after state is available
  useEffect(() => {
    if (processedCarsMap !== undefined) {
      processedCarsMapRef.current = processedCarsMap;
    }
  }, [processedCarsMap]);

  // Unified track management hook (fetching + subscriptions)
  const {
    init,
    getTrackCars: getShipments,
    refreshTracksDebounced,
    justCompletedDragRef: unifiedJustCompletedDragRef,
    setProcessedCarsMapWithRefSync,
  } = useTrackManagement({
    setTracks,
    setProcessedCarsMap,
    setStaleTracks,
    setLoading,
    tracks,
    setSelectedTrack,
    setSelectedShipment,
    notify,
    reloadYardId: window.appUser?.reloadYardId,
    isCrewApp: true,
    selectedInventoryItem,
    currentShipment,
    defaultShipmentId,
    isDraggingRef,
    handleDragEndFlushRef,
  });

  // Component lifecycle: Set mounted flag on mount, clear on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Clear any pending cleanup timeouts
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const initKey = [
      selectedInventoryItem?.aisleId || "",
      currentShipment?.id || "",
      currentShipment?.trackCar?.trackId || "",
      defaultShipmentId || "",
    ].join("|");
    if (lastInitKeyRef.current === initKey) return;
    lastInitKeyRef.current = initKey;

    const initialVisibleIds = [];
    if (selectedInventoryItem?.aisleId)
      initialVisibleIds.push(selectedInventoryItem.aisleId);
    if (currentShipment?.trackCar?.trackId)
      initialVisibleIds.push(currentShipment.trackCar.trackId);
    if (defaultShipmentId) {
      // If we have a default shipment, we might not know its track yet,
      // but init will fetch shipments anyway.
    }

    init({
      visibleTrackIds: initialVisibleIds.length > 0 ? initialVisibleIds : null,
    }).finally(() => {
      initCompleteRef.current = true;
      suppressInitialSelectionFetchRef.current = false;
      lastSelectedTrackLocationIdRef.current =
        selectedTrack?.locations?.items?.[0]?.id || null;
      lastTargetTrackLocationIdRef.current =
        targetTrack?.locations?.items?.[0]?.id || null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedInventoryItem?.aisleId,
    currentShipment?.id,
    currentShipment?.trackCar?.trackId,
    defaultShipmentId,
    init,
  ]);

  // REDUNDANT FETCH REMOVED:
  // TrackManagement.js's getTrackCars() already fetches the entire yard.
  // Re-fetching the entire yard whenever a single track is selected in the dropdown
  // was causing the "delay" and unnecessary network traffic.
  // Real-time updates are handled by subscriptions.

  useEffect(() => {
    if (!initCompleteRef.current) return;
    const selectedLocationId = selectedTrack?.locations?.items?.[0]?.id || null;
    const targetLocationId = targetTrack?.locations?.items?.[0]?.id || null;

    const changedIds = [];
    const side = lastChangedSideRef.current;
    if (
      (side === "left" || side == null) &&
      selectedLocationId &&
      selectedLocationId !== lastSelectedTrackLocationIdRef.current
    ) {
      changedIds.push(selectedLocationId);
    }
    if (
      (side === "right" || side == null) &&
      targetLocationId &&
      targetLocationId !== lastTargetTrackLocationIdRef.current
    ) {
      changedIds.push(targetLocationId);
    }

    lastSelectedTrackLocationIdRef.current = selectedLocationId;
    lastTargetTrackLocationIdRef.current = targetLocationId;
    lastChangedSideRef.current = null;

    if (!changedIds.length) return;

    Promise.all(
      changedIds.map((trackLocationId) => {
        setTrackLoading(trackLocationId, true);
        clearTrackCars(trackLocationId);
        return getShipments({
          showSpinner: false,
          skipLoading: true,
          visibleTrackIds: [trackLocationId],
        }).finally(() => {
          setTrackLoading(trackLocationId, false);
        });
      }),
    );
  }, [
    selectedTrack,
    targetTrack,
    getShipments,
    setTrackLoading,
    clearTrackCars,
  ]);

  const addToUnspottedLane = useCallback(
    (shipment) => {
      if (!shipment?.id) return;
      const base = {
        ...shipment,
        trackId: UNSPOTTED_TRACK_ID,
        sortKey: shipment.sortKey ?? "",
      };

      const sortUnspotted = (list = []) =>
        [...list].sort((a, b) => {
          const byNumber = (a.railCarNumber || "").localeCompare(
            b.railCarNumber || "",
          );
          if (byNumber !== 0) return byNumber;
          return (a.id || "").localeCompare(b.id || "");
        });

      // Use functional updates to avoid dependency on state values
      setProcessedCarsMap((prev) => {
        const existing = prev?.[UNSPOTTED_TRACK_ID] || [];
        // Guard: Don't add if already exists
        if (existing.some((c) => c.id === shipment.id)) return prev;
        return {
          ...prev,
          [UNSPOTTED_TRACK_ID]: sortUnspotted([...existing, base]),
        };
      });
    },
    [setProcessedCarsMap],
  );

  /**
   * Gets cars for a specific track location ID
   * Searches in processedCarsMap
   * @param {string} trackLocationId - The track location ID
   * @returns {Array} Array of cars for the track
   */
  const getTrackCarsFor = useCallback(
    (trackLocationId) => {
      if (!trackLocationId) return [];
      // STAFF LEVEL OPTIMIZATION: Return the list directly from the map to keep reference stable
      // if displayProcessedCarsMap is memoized correctly.
      return displayProcessedCarsMap[trackLocationId] || [];
    },
    [displayProcessedCarsMap],
  );

  // Wrap isOutbound utility in useCallback (TrackLocation relies on parentType only)
  const isOutbound = useCallback((shipment) => {
    return isOutboundUtil(shipment);
  }, []);

  // ============================================================================
  // DRAG & DROP CONFIGURATION
  // ============================================================================

  // Operations hook (must be called before useTrackDragDrop which needs saveShipment)
  const {
    addItem,
    removeFromTrack,
    deleteItem: deleteItemOperation,
    saveShipment,
    updateShipmentOnPlacement,
  } = useTrackLocationOperations({
    selectedShipment,
    selectedTrack,
    processedCarsMap,
    selectedTrackRef,
    processedCarsMapRef,
    setLoading,
    setProcessedCarsMap: setProcessedCarsMapWithRefSync,
    setSelectedShipment,
    isTabletOrLandscape,
    isOutbound,
    addToUnspottedLane,
    refreshTracksDebounced,
    getShipments,
    notify,
    notifyError,
    logError,
    currentShipment,
    tracks,
  });

  // Drag & Drop hook (must be called after useTrackLocationOperations to get saveShipment)
  const {
    handleDragStart: hookHandleDragStart,
    handleDragEnd: hookHandleDragEnd,
    handleDragOver: hookHandleDragOver,
  } = useTrackDragDrop({
    processedCarsMap,
    setProcessedCarsMap: setProcessedCarsMapWithRefSync,
    selectedShipmentIds,
    setSelectedShipmentIds,
    setLoading,
    saveShipment, // From useTrackLocationOperations
    notify,
    logError,
    redirect,
    setDragOverInfo: () => {}, // Not used here, passed empty
    refreshTracks: refreshTracksDebounced, // Debounced refresh to avoid multiple rapid calls
    unspottedTrackId: UNSPOTTED_TRACK_ID,
    getShipments, // From useTrackManagement
    updateShipmentOnPlacement, // For shipment status/dates during drag-drop placement
    tracks,
    setTrackData: setProcessedCarsMapWithRefSync,
    onSuccess: () => {
      // Clear selection after a successful drop to avoid lingering highlight colors
      setSelectedShipmentIds([]);
      setLastSelectedContext(null);
      setSelectedShipment(null);
    },
  });

  // Central openability predicate passed through selection + row-click layers.
  // This predicate is required by lower layers (no fallback behavior).
  // Open outbound when status is ARRIVED or PLACED and state is not COMPLETED (state may be null).
  // Open inbound only when status is PLACED and state is present and not COMPLETED.
  const canOpenShipment = useCallback(
    (trackCar) => {
      if (!trackCar) return false;
      const { status, state, sit } = getShipmentStatusFromTrackCar(trackCar);
      if (status == null) return false;
      if (sit) return false;

      if (isOutbound(trackCar)) {
        return (
          (status === OutboundStatus.ARRIVED ||
            status === OutboundStatus.PLACED) &&
          state !== OutboundState.COMPLETED
        );
      }
      if (state == null) return false;
      return (
        status === InboundStatus.PLACED && state !== InboundState.COMPLETED
      );
    },
    [isOutbound],
  );

  const openShipment = useCallback(
    (trackCar) => {
      if (!canOpenShipment(trackCar)) return;
      const shipmentId = trackCar.shipmentId ?? trackCar.id;
      if (isOutbound(trackCar)) {
        redirect("/processOutboundShipments/" + shipmentId);
      } else {
        redirect("/processShipments/" + shipmentId);
      }
    },
    [redirect, isOutbound, canOpenShipment],
  );

  // Use utility functions (wrapped for component context)
  const getShipmentClassNameLocal = useCallback(
    (shipment) =>
      getShipmentClassName(
        shipment,
        selectedShipmentIds,
        accountFilterId,
        defaultShipmentId,
        currentShipment,
        selectedShipment,
        classes,
      ),
    [
      selectedShipmentIds,
      accountFilterId,
      defaultShipmentId,
      currentShipment,
      selectedShipment,
      classes,
    ],
  );

  const getIconClassNameLocal = useCallback(
    (shipment) => getIconClassName(shipment, currentShipment, classes),
    [currentShipment, classes],
  );

  const getShipmentIconLocal = useCallback(
    (shipment) => getShipmentIcon(shipment, isOutbound, getIconClassNameLocal),
    [isOutbound, getIconClassNameLocal],
  );

  // ============================================================================
  // DRAG & DROP HANDLERS
  // ============================================================================

  // CRITICAL FIX: Wrap hook handlers with NO dependencies on changing state
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDragStart = useCallback(
    (event) => {
      // Clear any pending cleanup
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }

      // Set flag immediately to prevent subscription updates from interfering with drag
      isDraggingRef.current = true;

      // Call hook handler
      hookHandleDragStart(event);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [hookHandleDragStart],
  );
  // Note: Refs (processedCarsMapRef) are intentionally excluded

  const handleDragOver = useCallback(
    (event) => {
      // Call hook handler (it handles both tablet and mobile via map structure)
      hookHandleDragOver(event);
    },
    [hookHandleDragOver],
  );

  const handleDragEnd = useCallback(
    async (event) => {
      // It will be cleared after the operation completes
      try {
        await hookHandleDragEnd(event);
      } finally {
        isDraggingRef.current = false;
        // Set flag to prevent useEffect from triggering getShipments when tracks update
        // This prevents double refresh after drag operations
        unifiedJustCompletedDragRef.current = true;
        setSelectedShipmentIds([]);
        setLastSelectedContext(null);
        setSelectedShipment(null);
        // Do not clear target track (right track) on drag end so the user's selection is preserved

        if (cleanupTimeoutRef.current) {
          clearTimeout(cleanupTimeoutRef.current);
          cleanupTimeoutRef.current = null;
        }

        if (handleDragEndFlushRef.current) {
          handleDragEndFlushRef.current();
        }

        // Reset flag after a delay to allow refreshTracksDebounced to complete
        // Extended to 2000ms to ensure it covers the full refresh cycle:
        // - 500ms delay before refreshTracksDebounced is called
        // - 250ms debounce in refreshTracksDebounced
        // - Network request time for refreshSpecificTracks
        // This ensures the useEffect won't trigger getShipments when tracks update for capacity
        setTimeout(() => {
          unifiedJustCompletedDragRef.current = false;
        }, 2000);
      }
    },
    [
      hookHandleDragEnd,
      setLastSelectedContext,
      setSelectedShipment,
      setSelectedShipmentIds,
      unifiedJustCompletedDragRef,
    ],
  );

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDeleteConfirm = useCallback(
    (shipment, trackLocationId = null) => {
      if (!shipment?.id) return;
      const railCarLabel =
        getRailCarNumberFromTrackCar(shipment) || "this railcar";
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const resolvedTrackLocationId =
        trackLocationId ||
        shipment.trackCar?.trackId ||
        selectedTrackRef.current?.locations?.items?.[0]?.id ||
        null;

      setShipmentToDelete({
        id: shipment.id,
        trackLocationId: resolvedTrackLocationId,
      });
      setConfirmMsg(
        `This will mark railcar ${railCarLabel} as collected and permanently remove it from Track Management. Proceed?`,
      );
      setDeleteOpen(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
    // Note: selectedTrackRef is intentionally excluded - refs are stable and don't need to be in deps
  );

  const handleConfirmClose = useCallback(() => {
    setDeleteOpen(false);
    setShipmentToDelete(null);
  }, []);

  const deleteItem = useCallback(async () => {
    try {
      await deleteItemOperation(shipmentToDelete);
      setDeleteOpen(false);
      setShipmentToDelete(null);
    } catch (err) {
      // Error already handled in hook
    }
  }, [deleteItemOperation, shipmentToDelete]);

  const canAdd = selectedShipment && selectedShipment.id;
  const canDelete = useCallback((trackCar) => {
    const { status } = getShipmentStatusFromTrackCar(trackCar);
    if (status == null) return false;
    return (
      status === InboundStatus.RELEASED || status === OutboundStatus.RELEASED
    );
  }, []);

  // Selection hook (uses reducer state)
  // Note: This must come after getTrackCarsFor is defined
  const { handleShipmentSelect, handleShipmentLongPress } =
    useTrackLocationSelection(
      getTrackCarsFor,
      openShipment,
      selectedShipmentIds,
      setSelectedShipmentIds,
      lastSelectedContext,
      setLastSelectedContext,
      canOpenShipment,
    );

  const renderRailcarOverlay = useCallback(
    (item) => {
      // In tablet mode, search through all visible tracks to find the correct index
      // In mobile mode, search only in displayProcessedCars (selected track)
      let overlayIndex = -1;
      // Search through all tracks in processedCarsMap to find the car and its index
      for (const cars of Object.values(displayProcessedCarsMap)) {
        const foundIndex = cars.findIndex((c) => c.id === item.id);
        if (foundIndex !== -1) {
          overlayIndex = foundIndex;
          break;
        }
      }

      // Find trackLocationId for the active drag item
      let dragItemTrackLocationId = null;
      for (const [trackId, cars] of Object.entries(displayProcessedCarsMap)) {
        if (cars.find((c) => c.id === item.id)) {
          dragItemTrackLocationId = trackId;
          break;
        }
      }

      const isSelected = selectedIdsSet.has(item.id);

      return (
        <RailcarItem
          shipment={item}
          index={overlayIndex}
          trackLocationId={dragItemTrackLocationId}
          isOverlay={true}
          handlers={{
            openShipment,
            onShipmentClick: handleShipmentSelect,
            onShipmentLongPress: handleShipmentLongPress,
            handleDeleteConfirm,
            removeFromTrack,
            addItem,
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
          }}
          uiConfig={{
            getShipmentClassName: getShipmentClassNameLocal,
            getShipmentIcon: getShipmentIconLocal,
            isXSmall,
            showPlaceAction,
            colorsLoaded,
          }}
          selectionState={{
            isSelected,
            canDelete,
            canAdd,
            accountFilterId,
            canOpenShipment,
          }}
          isDraggingGlobal={true}
        />
      );
    },
    [
      displayProcessedCarsMap,
      selectedIdsSet,
      getShipmentClassNameLocal,
      getShipmentIconLocal,
      canDelete,
      canAdd,
      isXSmall,
      openShipment,
      handleShipmentSelect,
      handleShipmentLongPress,
      handleMouseEnter,
      handleMouseLeave,
      accountFilterId,
      handleDeleteConfirm,
      removeFromTrack,
      addItem,
      showPlaceAction,
      colorsLoaded,
      canOpenShipment,
    ],
  );

  const handleUnspottedSelect = useCallback(
    (unspottedId, unspottedCars) => {
      setSelectedUnspottedId(unspottedId);
      const car = unspottedCars.find((c) => c.id === unspottedId);
      if (car) {
        setSelectedShipment(car);
        setSelectedShipmentIds([car.id]);
      }
    },
    [setSelectedShipment, setSelectedShipmentIds],
  );

  const contextValue = useMemo(
    () => ({
      // State
      loading,
      setLoading,
      tracks,
      setTracks,
      selectedTrack,
      setSelectedTrack,
      targetTrack,
      setTargetTrack,
      setTargetTrackForControl,
      setTargetTrackId: setTargetTrackIdNoop,
      processedCarsMap,
      setProcessedCarsMap,
      staleTracks,
      selectedShipmentIds,
      setSelectedShipmentIds,
      selectedShipment,
      setSelectedShipment,
      accountFilter,
      setAccountFilter,
      productFilter,
      setProductFilter,
      accountFilterId,
      trackLoadingIds,
      isXSmall,
      showPlaceAction,
      isTabletOrLandscape,
      defaultShipmentId,
      currentShipment,
      colorsLoaded,
      onAccountsLoaded: handleAccountsLoaded,

      // UI Helpers & Classes
      getShipmentClassName: getShipmentClassNameLocal,
      getShipmentIcon: getShipmentIconLocal,
      classes,
      isTrackDisabled,

      // Capabilities & Handlers
      canDelete,
      canAdd,
      canOpenShipment,
      openShipment,
      onShipmentClick: handleShipmentSelect,
      onShipmentLongPress: handleShipmentLongPress,
      handleDeleteConfirm,
      removeFromTrack,
      addItem,
      handleTrackChange: handleTrackChangeForControl,
      getTrackCarsFor,
      handleUnspottedSelect,
      selectedUnspottedId,
      handleMouseEnter,
      handleMouseLeave,
      deleteOpen,
      confirmMsg,
      deleteItem,
      handleConfirmClose,
      displayProcessedCars,
      tooltipAnchor,
      tooltipTitle,
      selectedIdsSet,
      displayProcessedCarsMap,
    }),
    [
      handleTrackChangeForControl,
      loading,
      setLoading,
      tracks,
      setTracks,
      selectedTrack,
      setSelectedTrack,
      targetTrack,
      setTargetTrack,
      setTargetTrackForControl,
      setTargetTrackIdNoop,
      processedCarsMap,
      setProcessedCarsMap,
      staleTracks,
      selectedShipmentIds,
      setSelectedShipmentIds,
      selectedShipment,
      setSelectedShipment,
      accountFilter,
      setAccountFilter,
      productFilter,
      setProductFilter,
      accountFilterId,
      trackLoadingIds,
      isXSmall,
      showPlaceAction,
      isTabletOrLandscape,
      defaultShipmentId,
      currentShipment,
      colorsLoaded,
      handleAccountsLoaded,
      getShipmentClassNameLocal,
      getShipmentIconLocal,
      classes,
      canDelete,
      canOpenShipment,
      openShipment,
      handleShipmentSelect,
      handleShipmentLongPress,
      handleDeleteConfirm,
      removeFromTrack,
      addItem,
      canAdd,
      getTrackCarsFor,
      handleUnspottedSelect,
      selectedUnspottedId,
      handleMouseEnter,
      handleMouseLeave,
      deleteOpen,
      confirmMsg,
      deleteItem,
      handleConfirmClose,
      displayProcessedCars,
      tooltipAnchor,
      tooltipTitle,
      selectedIdsSet,
      displayProcessedCarsMap,
    ],
  );

  return (
    <TrackLocationProvider value={contextValue}>
      <TrackDndProvider
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        renderOverlay={renderRailcarOverlay}
        processedCarsMap={processedCarsMap}
      >
        <TrackLocationContent {...props} />
      </TrackDndProvider>
    </TrackLocationProvider>
  );
};

const TrackLocationContent = (props) => {
  const { activeItem } = useTrackDnd();
  const {
    loading,
    tracks,
    selectedTrack,
    targetTrack,
    processedCarsMap,
    staleTracks,
    setSelectedShipmentIds,
    setSelectedShipment,
    accountFilter,
    setAccountFilter,
    productFilter,
    setProductFilter,
    accountFilterId,
    trackLoadingIds,
    isXSmall,
    showPlaceAction,
    isTabletOrLandscape,
    defaultShipmentId,
    currentShipment,
    colorsLoaded,
    onAccountsLoaded: handleAccountsLoaded,
    classes,
    handleTrackChange,
    setTargetTrackForControl,
    setTargetTrackId,
    getTrackCarsFor,
    handleUnspottedSelect,
    selectedUnspottedId,
    addItem,
    handleMouseEnter,
    handleMouseLeave,
    deleteOpen,
    confirmMsg,
    deleteItem,
    handleConfirmClose,
    displayProcessedCars,
    tooltipAnchor,
    tooltipTitle,
    selectedIdsSet,
    displayProcessedCarsMap,
    getShipmentClassName,
    getShipmentIcon,
    canDelete,
    canOpenShipment,
    isTrackDisabled,
    openShipment,
    onShipmentClick,
    onShipmentLongPress,
    handleDeleteConfirm,
    removeFromTrack,
    selectedShipment,
  } = useTrackLocationContext();
  const [unspottedSearchTerm, setUnspottedSearchTerm] = useState("");
  const [unspottedVisibleCount, setUnspottedVisibleCount] =
    useState(UNSPOTTED_PAGE_SIZE);

  useEffect(() => {
    setUnspottedVisibleCount(UNSPOTTED_PAGE_SIZE);
  }, [unspottedSearchTerm]);

  const selectedTrackLocationId =
    selectedTrack?.locations?.items?.[0]?.id || null;
  const targetTrackLocationId = targetTrack?.locations?.items?.[0]?.id || null;
  const isLeftTrackLoading =
    selectedTrackLocationId && trackLoadingIds?.has(selectedTrackLocationId);
  const isRightTrackLoading =
    targetTrackLocationId && trackLoadingIds?.has(targetTrackLocationId);

  // UX Optimization: Check if we have any data to show a subtle loading state instead of a blocking one
  const hasData = useMemo(() => {
    return Object.values(processedCarsMap || {}).some(
      (cars) => cars && cars.length > 0,
    );
  }, [processedCarsMap]);

  // Common props for TrackBucket to avoid repetition and ensure stability
  const bucketProps = {
    selectedTrack,
    targetTrack,
    staleTracks,
    isDraggingGlobal: !!activeItem,
    classes,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    selectedIdsSet,
    getShipmentClassName,
    getShipmentIcon,
    canDelete,
    canAdd: !!(selectedShipment && selectedShipment.id),
    canOpenShipment,
    isXSmall,
    openShipment,
    onShipmentClick,
    onShipmentLongPress,
    accountFilterId,
    handleDeleteConfirm,
    removeFromTrack,
    addItem,
    showPlaceAction,
    defaultShipmentId,
    currentShipment,
    selectedShipment,
    colorsLoaded,
  };

  return (
    <Grid container spacing={2} style={{ position: "relative" }}>
      {/* Subtle loading indicator when data is already present */}
      {loading && hasData && (
        <Box position="absolute" top={0} left={0} right={0} zIndex={1000}>
          <LinearProgress />
        </Box>
      )}

      {/* Blocking indicator only when NO data is available */}
      <Backdrop className={classes.backdrop} open={loading && !hasData}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Grid item xs={12}>
        <TrackControl
          isTrackDisabled={isTrackDisabled}
          leftTrack={selectedTrack}
          handleLeftTrackChange={handleTrackChange}
          tracks={tracks}
          classes={classes}
          isTabletOrLandscape={isTabletOrLandscape}
          rightTrack={targetTrack}
          setRightTrack={setTargetTrackForControl}
          setRightTrackId={setTargetTrackId}
          accountFilter={accountFilter}
          setAccountFilter={setAccountFilter}
          productFilter={productFilter}
          setProductFilter={setProductFilter}
          onAccountsLoaded={handleAccountsLoaded}
          leftTrackLoading={Boolean(isLeftTrackLoading)}
          rightTrackLoading={Boolean(isRightTrackLoading)}
        />
      </Grid>
      {!isTabletOrLandscape && (
        <Grid item xs={12}>
          <Box className={classes.placeControls}>
            {(() => {
              const unspottedCarsFull = getTrackCarsFor(UNSPOTTED_TRACK_ID);
              const normalizedSearchTerm = unspottedSearchTerm
                .trim()
                .toLowerCase();
              const filteredUnspottedCars = normalizedSearchTerm
                ? unspottedCarsFull.filter((car) => {
                    const railCarNumber = (car?.railCarNumber || "")
                      .trim()
                      .toLowerCase();
                    const carName = (car?.name || "").trim().toLowerCase();
                    return (
                      railCarNumber.includes(normalizedSearchTerm) ||
                      carName.includes(normalizedSearchTerm)
                    );
                  })
                : unspottedCarsFull;
              // Keep dropdown render bounded while allowing incremental loading.
              const unspottedCars = filteredUnspottedCars.slice(
                0,
                unspottedVisibleCount,
              );
              const hasMoreUnspottedCars =
                filteredUnspottedCars.length > unspottedCars.length;
              const showingText = `Showing ${unspottedCars.length} of ${filteredUnspottedCars.length}`;

              return (
                <>
                  <TextField
                    variant="outlined"
                    margin="dense"
                    label="Filter unspotted railcars"
                    placeholder="Railcar # or name"
                    value={unspottedSearchTerm}
                    onChange={(e) => setUnspottedSearchTerm(e.target.value)}
                    className={classes.placeSelect}
                  />
                  <TextField
                    select
                    variant="outlined"
                    margin="dense"
                    label="Select a railcar to place"
                    helperText={
                      hasMoreUnspottedCars
                        ? `${showingText}. Use filter or load more.`
                        : showingText
                    }
                    value={selectedUnspottedId}
                    onChange={(e) =>
                      handleUnspottedSelect(e.target.value, unspottedCarsFull)
                    }
                    className={classes.placeSelect}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {unspottedCars.map((car) => (
                      <MenuItem key={car.id} value={car.id}>
                        {car.railCarNumber || car.name || car.id}
                      </MenuItem>
                    ))}
                  </TextField>
                  {hasMoreUnspottedCars && (
                    <Button
                      size="small"
                      color="primary"
                      onClick={() =>
                        setUnspottedVisibleCount(
                          (prev) => prev + UNSPOTTED_PAGE_SIZE,
                        )
                      }
                    >
                      Load {UNSPOTTED_PAGE_SIZE} more
                    </Button>
                  )}
                </>
              );
            })()}
            <Button
              variant="contained"
              color="primary"
              className={classes.placeButton}
              startIcon={<SubdirectoryArrowLeftIcon />}
              disabled={
                !selectedUnspottedId ||
                !selectedTrackLocationId ||
                selectedTrackLocationId === UNSPOTTED_TRACK_ID
              }
              onClick={() => {
                const unspottedCars = getTrackCarsFor(UNSPOTTED_TRACK_ID);
                const car = unspottedCars.find(
                  (c) => c.id === selectedUnspottedId,
                );
                if (car) {
                  setSelectedShipment(car);
                  setSelectedShipmentIds([car.id]);
                  addItem(-1);
                }
              }}
            >
              Place
            </Button>
          </Box>
        </Grid>
      )}
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <TrackLegend selectedTrack={currentShipment} />
        {isTabletOrLandscape ? (
          <Grid container spacing={2}>
            {[
              tracks.find((track) => track.id === UNSPOTTED_TRACK_ID),
              selectedTrack,
              targetTrack,
            ]
              .filter(Boolean)
              .filter(
                (track, index, list) =>
                  list.findIndex((t) => t.id === track.id) === index,
              )
              .map((track) => {
                const trackLocationId = track?.locations?.items?.[0]?.id;
                const trackCars =
                  displayProcessedCarsMap[trackLocationId] || [];
                const fullCarsOnTrack = processedCarsMap[trackLocationId] || [];
                const isTrackLoading =
                  trackLocationId && trackLoadingIds?.has(trackLocationId);

                return (
                  <TrackBucket
                    key={track?.id || "track-bucket-" + trackLocationId}
                    track={track}
                    trackCars={trackCars}
                    fullCarsOnTrack={fullCarsOnTrack}
                    isLoading={Boolean(isTrackLoading)}
                    {...bucketProps}
                  />
                );
              })}
          </Grid>
        ) : (
          <TrackBucket
            track={selectedTrack}
            trackCars={displayProcessedCars}
            fullCarsOnTrack={(() => {
              const tid = selectedTrack?.locations?.items?.[0]?.id;
              return tid ? processedCarsMap[tid] || [] : [];
            })()}
            isLoading={Boolean(isLeftTrackLoading)}
            {...bucketProps}
          />
        )}
      </Grid>
      <Confirm
        isOpen={deleteOpen}
        loading={loading}
        title="Delete Confirmation"
        content={confirmMsg}
        onConfirm={deleteItem}
        onClose={handleConfirmClose}
      />

      {/* STAFF LEVEL OPTIMIZATION: Singleton Tooltip */}
      <Popper
        open={!!tooltipAnchor}
        anchorEl={tooltipAnchor}
        placement="top"
        style={{ zIndex: 1500, pointerEvents: "none" }}
        modifiers={{
          flip: { enabled: true },
          preventOverflow: { enabled: true, boundariesElement: "viewport" },
          arrow: { enabled: true },
        }}
      >
        <Paper
          elevation={4}
          style={{
            padding: "4px 8px",
            backgroundColor: "rgba(97, 97, 97, 0.92)",
            color: "#fff",
            fontSize: "0.75rem",
            maxWidth: 300,
            borderRadius: 4,
          }}
        >
          {tooltipTitle}
        </Paper>
      </Popper>
    </Grid>
  );
};

const TrackLocationContainer = (props) => {
  const returnPath = props.location?.state?.returnPath;
  const redirect = useRedirect();

  return (
    <ErrorBoundary componentName="TrackLocation">
      <Title title={<ForceTitle title="Track" />} />
      <CrewCard
        title="Track Management"
        icon={<TrainIcon fontSize="large" />}
        showDivider={false}
        action={
          returnPath && (
            <IconButton edge="start" onClick={() => redirect(returnPath)}>
              <HighlightOffIcon fontSize="large" />
            </IconButton>
          )
        }
      >
        <TrackLocation {...props} />
      </CrewCard>
    </ErrorBoundary>
  );
};

export { TrackLocationContainer };
